import { useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { useSearchParams } from "react-router-dom";
import { Check, CheckCheck, Link2, Loader2, Sparkles, StickyNote, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FilterBar, PageHeader } from "@/components/FilterBar";
import { KpiCard, Panel } from "@/components/KpiCard";
import { MatchBadge, PlatformBadge } from "@/components/Badges";
import { ManualMatchDialog } from "@/components/ManualMatchDialog";
import { FacturesBookingPanel } from "@/components/FacturesBookingPanel";
import { RapportButtons } from "@/components/RapportButtons";
import { TaxesManquantesPanel } from "@/components/TaxesManquantesPanel";
import { CommissionsEnTropPanel } from "@/components/CommissionsEnTropPanel";
import { EcartNote } from "@/components/EcartNote";
import { CommissionFlag } from "@/components/CommissionFlag";
import { SuggestionCard } from "@/components/SuggestionCard";
import { PeriodPicker, periodeParams } from "@/components/PeriodPicker";
import { useFilters } from "@/context/FilterContext";
import { api, cleanParams, errMsg, money } from "@/lib/api";

const Th = ({ children, right }) => <th className={`py-2 px-3 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>;

const DepotActions = ({ d, act, onManual }) => {
  if (!d.match && d.regularisation_code)
    return <span className="rounded bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-800 whitespace-nowrap" data-testid={`depot-regularisation-${d.id}`}>Régularisation {d.regularisation_code}</span>;
  if (!d.match)
    return <Button size="sm" variant="outline" className="h-8" onClick={() => onManual(d)} data-testid={`associate-btn-${d.id}`}><Link2 className="h-3.5 w-3.5 mr-1" />Associer</Button>;
  return (
    <div className="flex justify-end gap-1">
      {d.match.statut === "propose" && (
        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-700 hover:bg-emerald-50" onClick={() => act("post", `/matches/${d.match.id}/valider`, "Rapprochement validé")} data-testid={`validate-match-${d.id}`}><Check className="h-4 w-4" /></Button>
      )}
      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => act("delete", `/matches/${d.match.id}`, d.match.statut === "propose" ? "Proposition rejetée" : "Rapprochement annulé")} data-testid={`reject-match-${d.id}`}><X className="h-4 w-4" /></Button>
    </div>
  );
};

const NoteInterne = ({ r }) => (r.note_interne ? (
  <p className="mt-0.5 flex items-start gap-1 rounded bg-amber-50 px-1.5 py-1 text-[11px] leading-snug text-amber-900" data-testid={`rappro-note-${r.id}`}>
    <StickyNote className="h-3 w-3 mt-0.5 shrink-0 text-amber-600" />{r.note_interne}
  </p>
) : null);

const Reservations = ({ d }) => {
  if (!d.reservations.length) return <span className="text-xs text-slate-400">Aucune correspondance</span>;
  return d.reservations.map((r) => (
    <div key={r.id}>
      <p className="text-xs"><span className="font-mono">{r.code}</span> <span className="text-slate-500">· {money(r.total_verse)}</span><CommissionFlag r={r} /></p>
      <NoteInterne r={r} />
    </div>
  ));
};

const SURLIGNE = "bg-amber-50 outline outline-2 -outline-offset-2 outline-amber-400 transition-colors duration-700";

const DepotRow = ({ d, act, onManual, reload, surligne }) => (
  <tr className={`border-t border-slate-100 hover:bg-slate-50/70 align-top ${surligne ? SURLIGNE : ""}`} data-testid={`depot-row-${d.id}`}>
    <td className="py-3 px-3 tabular text-xs whitespace-nowrap">{d.date}</td>
    <td className="py-3 px-3"><p className="font-medium">{d.description}</p><PlatformBadge plateforme={d.plateforme_detectee} /></td>
    <td className="py-3 px-3 text-right tabular font-semibold text-emerald-700">{money(d.credit)}</td>
    <td className="py-3 px-3"><Reservations d={d} /></td>
    <td className="py-3 px-3 text-xs tabular whitespace-nowrap text-slate-600" data-testid={`depot-sejour-${d.id}`}>
      {d.reservations.length ? d.reservations.map((r) => <p key={r.id}>{r.date_debut}{r.date_fin ? ` → ${r.date_fin}` : ""}</p>) : "—"}
    </td>
    <td className={`py-3 px-3 text-right tabular text-xs ${d.match && Math.abs(d.match.ecart) > 0.009 ? "text-amber-700" : "text-slate-500"}`}>{d.match ? money(d.match.ecart) : "—"}</td>
    <td className="py-3 px-3"><EcartNote txId={d.id} explication={d.explication} note={d.note} onSaved={reload} /></td>
    <td className="py-3 px-3 whitespace-nowrap">{!d.match && d.regularisation_code
      ? <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-mono uppercase text-emerald-800" data-testid={`depot-status-${d.id}`}>Régularisation</span>
      : <MatchBadge match={d.match} testId={`depot-status-${d.id}`} />}</td>
    <td className="py-3 px-3 text-right"><DepotActions d={d} act={act} onManual={onManual} /></td>
  </tr>
);

export default function Rapprochement() {
  const { filters, options, resetFilters } = useFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cible, setCible] = useState(null);
  const [periode, setPeriode] = useState(null);
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [manual, setManual] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const params = periodeParams(periode);

  useEffect(() => {
    const depot = searchParams.get("depot");
    const resa = searchParams.get("resa");
    const date = searchParams.get("date") || "";
    if ((depot || resa) && /^\d{4}-\d{2}/.test(date)) {
      resetFilters();
      setCible(depot ? `depot-row-${depot}` : `unmatched-res-${resa}`);
      setPeriode({ mode: date.slice(0, 7) });
      setSearchParams({}, { replace: true });
      return;
    }
    api.get("/rapprochement/periode").then((r) => setPeriode({ mode: r.data.fin ? r.data.fin.slice(0, 7) : "all" }))
      .catch(() => setPeriode({ mode: "all" }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cible || !data) { return undefined; }
    const el = document.querySelector(`[data-testid="${cible}"]`);
    if (!el) {
      toast.error("Opération introuvable sur cette période du rapprochement");
      setCible(null);
      return undefined;
    }
    const viser = () => document.querySelector(`[data-testid="${cible}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    viser();
    // les panneaux du dessus se chargent après : on garde la ligne visible tant que l'utilisateur ne fait pas défiler
    const obs = new ResizeObserver(viser);
    obs.observe(document.body);
    const arreter = () => obs.disconnect();
    const evts = ["wheel", "touchmove", "keydown"];
    evts.forEach((e) => window.addEventListener(e, arreter, { once: true }));
    const t = setTimeout(() => setCible(null), 6000);
    return () => { clearTimeout(t); arreter(); evts.forEach((e) => window.removeEventListener(e, arreter)); };
  }, [cible, data]);

  const load = useCallback(() => {
    if (!periode) return;
    api.get("/rapprochement", { params: cleanParams({ plateforme: filters.plateforme, logement: filters.logement, ...periodeParams(periode) }) })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/associations/suggestions", { params: periodeParams(periode) }).then((r) => setSuggestions(r.data)).catch(() => setSuggestions([]));
  }, [filters.plateforme, filters.logement, periode]);
  useEffect(load, [load]);

  const act = async (method, url, msg) => {
    try { await api[method](url); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const runAuto = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/rapprochement/auto");
      toast.success(`${r.propositions} proposition(s) : ${r.individuels} individuelle(s), ${r.groupes} virement(s) groupé(s)${r.factures ? ` · ${r.factures} facture(s) Booking` : ""}`);
      setRefreshKey((k) => k + 1);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };

  if (!data || !periode) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;
  const s = data.stats;

  return (
    <div data-testid="rapprochement-page">
      <PageHeader eyebrow="Étape 2" title="Rapprochement bancaire">
        <div className="flex flex-col gap-2 items-start xl:items-end">
          <div className="flex flex-wrap gap-2">
            <Button onClick={runAuto} disabled={running} className="bg-emerald-700 hover:bg-emerald-800" data-testid="run-auto-match-button">
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Proposer automatiquement
            </Button>
            <RapportButtons periode={{ ...params, ...cleanParams({ plateforme: filters.plateforme, logement: filters.logement }) }} />
            <Button variant="outline" disabled={!s.proposes} onClick={() => act("post", "/matches/valider-tout", "Toutes les propositions sont validées")} data-testid="validate-all-button">
              <CheckCheck className="h-4 w-4 mr-2" />Tout valider ({s.proposes})
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <PeriodPicker value={periode} onChange={setPeriode} mois={options.mois} />
            <FilterBar showMois={false} />
          </div>
        </div>
      </PageHeader>
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Dépôts" value={s.depots} testId="stat-depots" />
        <KpiCard label="Validés" value={s.valides} tone="good" testId="stat-valides" />
        <KpiCard label="À valider" value={s.proposes} tone={s.proposes ? "warn" : "default"} testId="stat-proposes" />
        <KpiCard label="Sans correspondance" value={s.sans_correspondance} tone={s.sans_correspondance ? "danger" : "default"} testId="stat-sans-correspondance" />
        <KpiCard label="Commissions hors taux" value={s.commissions_hors_15} tone={s.commissions_hors_15 ? "warn" : "default"} testId="stat-commissions-hors-15" />
      </div>
      <Panel title="Dépôts bancaires" testId="depots-panel">
        <div className="overflow-x-auto -mx-5">
          <SmartTable id="depots" className="w-full text-sm min-w-[1200px]" data-testid="depots-table">
            <thead><tr><Th>Date</Th><Th>Description</Th><Th right>Montant</Th><Th>Réservations</Th><Th>Dates séjour</Th><Th right>Écart</Th><Th>Explication de l'écart</Th><Th>Statut</Th><Th right>Actions</Th></tr></thead>
            <tbody>
              {data.depots.map((d) => <DepotRow key={d.id} d={d} act={act} onManual={setManual} reload={load} surligne={cible === `depot-row-${d.id}`} />)}
              {!data.depots.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Aucun dépôt sur cette période. Importez un relevé bancaire ou changez de période.</td></tr>}
            </tbody>
          </SmartTable>
        </div>
      </Panel>
      <FacturesBookingPanel periode={params} refreshKey={refreshKey} />
      <TaxesManquantesPanel periode={params} refreshKey={refreshKey} />
      <CommissionsEnTropPanel periode={params} refreshKey={refreshKey} />
      {suggestions.length > 0 && (
        <Panel title={`Groupes suggérés (${suggestions.length})`} className="mt-6" testId="suggestions-panel">
          <p className="mb-3 text-xs text-slate-500">Réservations sans dépôt dont le total correspond à un dépôt libre, à 5 % près. Vérifiez puis associez.</p>
          <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
            {suggestions.map((s) => <SuggestionCard key={s.depot.id} s={s} onDone={load} />)}
          </div>
        </Panel>
      )}
      <Panel title={`Réservations sans dépôt (${data.reservations_non_rapprochees.length})`} className="mt-6" testId="unmatched-reservations-panel">
        {data.reservations_non_rapprochees.length === 0 ? <p className="text-sm text-slate-500">Toutes les réservations ont un dépôt associé ou proposé.</p> : (
          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {data.reservations_non_rapprochees.map((r) => (
              <li key={r.id} className={`flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 ${cible === `unmatched-res-${r.id}` ? SURLIGNE : ""}`} data-testid={`unmatched-res-${r.id}`}>
                <div><p className="text-sm font-mono">{r.code}</p><p className="text-xs text-slate-500">{r.date} · {r.logement || "—"}</p><CommissionFlag r={r} /><NoteInterne r={r} /></div>
                <div className="text-right"><p className="tabular text-sm">{money(r.total_verse)}</p><PlatformBadge plateforme={r.plateforme} /></div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <ManualMatchDialog depot={manual} reservations={data.reservations_non_rapprochees} onClose={() => setManual(null)} onDone={() => { setManual(null); load(); }} />
    </div>
  );
}
